import React, { useEffect, useMemo, useState } from 'react';
import { http } from "../lib/axios";
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AdminWorkspaceNav from '../components/AdminWorkspaceNav';
import ContextHelpButton from '../components/ContextHelpButton';

const ART_PREFERENCE_OPTIONS = [
  'Painting',
  'Sculpture',
  'Photography',
  'Mixed Media',
  'Digital',
  'Installation',
  'Drawing',
  'Print',
];

const glassPanelSx = {
  backgroundColor: 'rgba(255,255,255,0.72)',
  backdropFilter: 'blur(14px)',
  borderRadius: 3,
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
};

function profileCompleteness(artist) {
  const checks = [
    artist?.user?.first_name,
    artist?.user?.last_name,
    artist?.about,
    artist?.portfolio_link,
    artist?.address,
    Array.isArray(artist?.art_preferences) && artist.art_preferences.length > 0,
    artist?.location,
  ];
  const complete = checks.filter(Boolean).length;
  return Math.round((complete / checks.length) * 100);
}

function artistDisplayName(artist) {
  const first = artist?.user?.first_name || '';
  const last = artist?.user?.last_name || '';
  return `${first} ${last}`.trim() || artist?.user?.username || 'Unnamed Artist';
}

function missingArtistFields(artist) {
  const missing = [];
  if (!artist?.about) missing.push('About');
  if (!artist?.portfolio_link) missing.push('Portfolio link');
  if (!artist?.address) missing.push('Address');
  if (!artist?.recognition) missing.push('Recognition');
  if (!Array.isArray(artist?.art_preferences) || artist.art_preferences.length === 0) {
    missing.push('Art preferences');
  }
  return missing;
}

function payoutReadiness(artist) {
  if (artist?.payouts_enabled) return 'ready';
  if (artist?.stripe_account_id || artist?.details_submitted) return 'action_needed';
  return 'not_connected';
}

function payoutReadinessLabel(state) {
  if (state === 'ready') return 'Payouts Ready';
  if (state === 'action_needed') return 'Stripe Action Needed';
  return 'Stripe Not Connected';
}

function payoutReadinessColor(state) {
  if (state === 'ready') return 'success';
  if (state === 'action_needed') return 'warning';
  return 'default';
}

export default function ArtistManager() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [preferenceFilter, setPreferenceFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all');
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [editingArtist, setEditingArtist] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const artistFocusId = searchParams.get('artist') || 'all';

  const loadArtists = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const [artistsResponse, artworksResponse] = await Promise.all([
        http.get('/artists/'),
        http.get('/artworks/?view=card'),
      ]);
      setArtists(Array.isArray(artistsResponse.data) ? artistsResponse.data : []);
      setArtworks(Array.isArray(artworksResponse.data) ? artworksResponse.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Unable to load artist profiles.');
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadArtists();
  }, []);

  const filteredArtists = useMemo(() => {
    const q = search.trim().toLowerCase();
    return artists.filter((artist) => {
      const haystack = [
        artistDisplayName(artist),
        artist?.user?.username,
        artist?.user?.email,
        artist?.about,
        artist?.recognition,
        artist?.portfolio_link,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch = !q || haystack.includes(q);
      const preferenceMatch =
        preferenceFilter === 'all' ||
        (Array.isArray(artist?.art_preferences) && artist.art_preferences.includes(preferenceFilter));
      const completeness = profileCompleteness(artist);
      const completenessMatch =
        completenessFilter === 'all' ||
        (completenessFilter === 'attention' && completeness < 70) ||
        (completenessFilter === 'strong' && completeness >= 70);
      const artistFocusMatch = artistFocusId === 'all' || String(artist?.id) === String(artistFocusId);

      return searchMatch && preferenceMatch && completenessMatch && artistFocusMatch;
    });
  }, [artists, search, preferenceFilter, completenessFilter, artistFocusId]);

  const artistStatsById = useMemo(() => {
    const stats = {};
    artworks.forEach((artwork) => {
      const artistId = artwork?.artist?.id;
      if (!artistId) return;
      if (!stats[artistId]) {
        stats[artistId] = {
          artworkCount: 0,
          soldCount: 0,
          artflowHostedCount: 0,
          artflowDirectCount: 0,
          galleryRepresentedCount: 0,
          artworks: [],
        };
      }
      stats[artistId].artworkCount += 1;
      if (artwork?.status === 'sold') stats[artistId].soldCount += 1;
      if (artwork?.representation_mode === 'gallery_represented') {
        stats[artistId].galleryRepresentedCount += 1;
      } else if (artwork?.status === 'hosted') {
        stats[artistId].artflowHostedCount += 1;
      } else {
        stats[artistId].artflowDirectCount += 1;
      }
      stats[artistId].artworks.push(artwork);
    });
    return stats;
  }, [artworks]);

  const summary = useMemo(() => {
    const withPortfolio = artists.filter((artist) => artist?.portfolio_link).length;
    const needsAttention = artists.filter((artist) => profileCompleteness(artist) < 70).length;
    const withArtworks = artists.filter((artist) => (artistStatsById[artist.id]?.artworkCount || 0) > 0).length;
    const payoutsReady = artists.filter((artist) => payoutReadiness(artist) === 'ready').length;
    const payoutActionNeeded = artists.filter((artist) => payoutReadiness(artist) === 'action_needed').length;
    const avgCompleteness = artists.length
      ? Math.round(artists.reduce((sum, artist) => sum + profileCompleteness(artist), 0) / artists.length)
      : 0;

    return {
      total: artists.length,
      withPortfolio,
      withArtworks,
      payoutsReady,
      payoutActionNeeded,
      avgCompleteness,
      needsAttention,
    };
  }, [artists, artistStatsById]);

  const openEditDialog = (artist) => {
    setEditingArtist(artist);
    setEditForm({
      user: {
        first_name: artist?.user?.first_name || '',
        last_name: artist?.user?.last_name || '',
      },
      about: artist?.about || '',
      exhibitions: artist?.exhibitions || '',
      recognition: artist?.recognition || '',
      portfolio_link: artist?.portfolio_link || '',
      address: artist?.address || '',
      art_preferences: Array.isArray(artist?.art_preferences) ? artist.art_preferences : [],
    });
  };

  const closeEditDialog = () => {
    if (saving) return;
    setEditingArtist(null);
    setEditForm(null);
  };

  const handleEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserField = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!editingArtist?.id || !editForm) return;
    setSaving(true);
    try {
      await http.patch(`/artists/${editingArtist.id}/`, editForm);
      await loadArtists({ silent: true });
      setSnackbar({ open: true, message: 'Artist profile updated.', severity: 'success' });
      closeEditDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to update the artist profile.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await http.delete(`/artists/${deleteTarget.id}/`);
      setArtists((prev) => prev.filter((artist) => artist.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSnackbar({ open: true, message: 'Artist profile deleted.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to delete the artist profile.',
        severity: 'error',
      });
    }
  };

  return (
    <Container sx={{ mt: 5, mb: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              Artist Manager
            </Typography>
            <ContextHelpButton section="admin-help" title="Help for artist manager" />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage existing artist profiles, review profile completeness, and update the current artist-facing fields that actually drive the live product.
          </Typography>
        </Box>

        <AdminWorkspaceNav subtitle="Manage artist profiles inside the shared admin workspace. Routes and navigation are now consistent across the manager pages." />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Artists</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">With Portfolio Link</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.withPortfolio}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Average Profile Completeness</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.avgCompleteness}%</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Needs Attention</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.needsAttention}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Artists With Artworks</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.withArtworks}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Payouts Ready</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.payoutsReady}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Stripe Action Needed</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.payoutActionNeeded}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ ...glassPanelSx, p: 2.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search artists"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, username, email, about, recognition..."
              fullWidth
            />
            <TextField
              select
              label="Art preference"
              value={preferenceFilter}
              onChange={(event) => setPreferenceFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 220 } }}
            >
              <MenuItem value="all">All preferences</MenuItem>
              {ART_PREFERENCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Completeness"
              value={completenessFilter}
              onChange={(event) => setCompletenessFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 200 } }}
            >
              <MenuItem value="all">All profiles</MenuItem>
              <MenuItem value="attention">Needs attention</MenuItem>
              <MenuItem value="strong">70%+ complete</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                setPreferenceFilter('all');
                setCompletenessFilter('all');
              }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" onClick={() => loadArtists({ silent: true })} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredArtists.length} of {artists.length} artist profiles.
          </Typography>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Paper sx={{ ...glassPanelSx, p: 3 }}>
            <Typography>Loading artist profiles…</Typography>
          </Paper>
        ) : filteredArtists.length === 0 ? (
          <Paper sx={{ ...glassPanelSx, p: 3 }}>
            <Typography color="text.secondary">No artist profiles match the current filters.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredArtists.map((artist) => (
              <Grid item xs={12} key={artist.id}>
                {(() => {
                  const stats = artistStatsById[artist.id] || { artworkCount: 0, soldCount: 0, artflowHostedCount: 0, artflowDirectCount: 0, galleryRepresentedCount: 0 };
                  return (
                <Paper sx={{ ...glassPanelSx, p: 2.5 }}>
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', lg: 'stretch' }}
                    sx={{ minHeight: { lg: 220 } }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {artistDisplayName(artist)}
                        </Typography>
                        <Chip size="small" label={`Completeness ${profileCompleteness(artist)}%`} />
                        {profileCompleteness(artist) < 70 && (
                          <Chip size="small" color="warning" label="Needs attention" />
                        )}
                        {artist?.portfolio_link && <Chip size="small" color="success" label="Portfolio linked" />}
                        <Chip
                          size="small"
                          color={payoutReadinessColor(payoutReadiness(artist))}
                          label={payoutReadinessLabel(payoutReadiness(artist))}
                        />
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        @{artist?.user?.username} | {artist?.user?.email || 'No email'}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {artist?.about || 'No artist statement yet.'}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
                        <Chip size="small" label={`${stats.artworkCount} artworks`} />
                        <Chip size="small" label={`${stats.artflowHostedCount} ARTflow hosted`} />
                        <Chip size="small" label={`${stats.artflowDirectCount} direct ARTflow`} />
                        <Chip size="small" label={`${stats.galleryRepresentedCount} gallery represented`} />
                        <Chip size="small" label={`${stats.soldCount} sold`} />
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {(artist?.art_preferences || []).length > 0 ? (
                          artist.art_preferences.map((preference) => (
                            <Chip key={`${artist.id}-${preference}`} size="small" variant="outlined" label={preference} />
                          ))
                        ) : (
                          <Chip size="small" variant="outlined" label="No art preferences" />
                        )}
                      </Stack>

                      <Grid container spacing={1.5} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Address</Typography>
                          <Typography variant="body2">{artist?.address || 'Not provided'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Recognition</Typography>
                          <Typography variant="body2">{artist?.recognition || 'None listed'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Portfolio</Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {artist?.portfolio_link || 'Not provided'}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Stack
                        direction={{ xs: 'row', sm: 'row' }}
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 2 }}
                      >
                        <Button variant="outlined" onClick={() => navigate(`/admin/artworks?artist=${artist.id}`)}>
                          View Artworks
                        </Button>
                        <Button variant="outlined" onClick={() => navigate(`/artists/${artist.id}`)}>
                          Open Public Profile
                        </Button>
                        <Button variant="outlined" onClick={() => setSelectedArtist(artist)}>
                          View Details
                        </Button>
                        <Button variant="outlined" onClick={() => openEditDialog(artist)}>
                          Edit
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => setDeleteTarget(artist)}>
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
                  );
                })()}
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <Dialog open={Boolean(selectedArtist)} onClose={() => setSelectedArtist(null)} fullWidth maxWidth="md">
        <DialogTitle>Artist Profile Details</DialogTitle>
        <DialogContent dividers>
          {selectedArtist && (
            <Stack spacing={2}>
              {(() => {
                const stats = artistStatsById[selectedArtist.id] || { artworkCount: 0, soldCount: 0, artflowHostedCount: 0, artflowDirectCount: 0, galleryRepresentedCount: 0, artworks: [] };
                return (
                  <>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {artistDisplayName(selectedArtist)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  @{selectedArtist?.user?.username} | {selectedArtist?.user?.email || 'No email'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Completeness ${profileCompleteness(selectedArtist)}%`} />
                <Chip
                  color={selectedArtist?.portfolio_link ? 'success' : 'default'}
                  label={selectedArtist?.portfolio_link ? 'Portfolio linked' : 'Portfolio missing'}
                />
                <Chip
                  color={payoutReadinessColor(payoutReadiness(selectedArtist))}
                  label={payoutReadinessLabel(payoutReadiness(selectedArtist))}
                />
                <Chip label={`${stats.artworkCount} artworks`} />
                <Chip label={`${stats.artflowHostedCount} ARTflow hosted`} />
                <Chip label={`${stats.artflowDirectCount} direct ARTflow`} />
                <Chip label={`${stats.galleryRepresentedCount} gallery represented`} />
                <Chip label={`${stats.soldCount} sold`} />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">About</Typography>
                  <Typography variant="body2">{selectedArtist?.about || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2">{selectedArtist?.address || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Exhibitions</Typography>
                  <Typography variant="body2">{selectedArtist?.exhibitions || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Recognition</Typography>
                  <Typography variant="body2">{selectedArtist?.recognition || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Portfolio link</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedArtist?.portfolio_link || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Stripe account</Typography>
                  <Typography variant="body2">
                    {selectedArtist?.stripe_account_id || 'Not connected'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Payout readiness</Typography>
                  <Typography variant="body2">
                    {payoutReadinessLabel(payoutReadiness(selectedArtist))}
                  </Typography>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Art Preferences</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(selectedArtist?.art_preferences || []).length > 0 ? (
                    selectedArtist.art_preferences.map((preference) => (
                      <Chip key={`${selectedArtist.id}-${preference}`} size="small" variant="outlined" label={preference} />
                    ))
                  ) : (
                    <Chip size="small" variant="outlined" label="No art preferences" />
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Artworks</Typography>
                <Stack spacing={1}>
                  {stats.artworks.length > 0 ? (
                    stats.artworks.slice(0, 5).map((artwork) => (
                      <Paper key={artwork.id} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {artwork.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status: {artwork.status} {artwork.price ? `| Price: $${artwork.price}` : ''}
                        </Typography>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No artworks found for this artist yet.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Alert severity={missingArtistFields(selectedArtist).length > 0 ? 'warning' : 'success'}>
                {missingArtistFields(selectedArtist).length > 0
                  ? `Missing profile details: ${missingArtistFields(selectedArtist).join(', ')}`
                  : 'This artist profile has the core fields filled in.'}
              </Alert>
                  </>
                );
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/admin/artworks?artist=${selectedArtist?.id || ''}`)} disabled={!selectedArtist?.id}>
            View Artworks
          </Button>
          <Button onClick={() => navigate(`/artists/${selectedArtist?.id || ''}`)} disabled={!selectedArtist?.id}>
            Open Public Profile
          </Button>
          <Button onClick={() => setSelectedArtist(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = selectedArtist;
              setSelectedArtist(null);
              if (target) openEditDialog(target);
            }}
          >
            Edit Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingArtist)} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle>Edit Artist Profile</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="First name"
                  value={editForm.user.first_name}
                  onChange={(event) => handleUserField('first_name', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Last name"
                  value={editForm.user.last_name}
                  onChange={(event) => handleUserField('last_name', event.target.value)}
                  fullWidth
                />
              </Stack>
              <TextField
                label="About"
                value={editForm.about}
                onChange={(event) => handleEditField('about', event.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
              <TextField
                label="Exhibitions"
                value={editForm.exhibitions}
                onChange={(event) => handleEditField('exhibitions', event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <TextField
                label="Recognition"
                value={editForm.recognition}
                onChange={(event) => handleEditField('recognition', event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <TextField
                label="Portfolio link"
                value={editForm.portfolio_link}
                onChange={(event) => handleEditField('portfolio_link', event.target.value)}
                fullWidth
              />
              <TextField
                label="Address"
                value={editForm.address}
                onChange={(event) => handleEditField('address', event.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Art preferences"
                SelectProps={{
                  multiple: true,
                  value: editForm.art_preferences,
                  onChange: (event) => handleEditField('art_preferences', event.target.value),
                  renderValue: (selected) => selected.join(', '),
                }}
                fullWidth
              >
                {ART_PREFERENCE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Artist Profile</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Delete the artist profile for <strong>{deleteTarget ? artistDisplayName(deleteTarget) : ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This is an admin action and may remove live profile data, so it should be used carefully.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
